/* @flow */

import {
  SET_TAGS,
  ADD_TAG,
  UPDATE_TAG,
  DELETE_TAG,
  SHOW_TAG_PICKER,
  HIDE_TAG_PICKER,
  FORCE_TAG_PICKER_OPEN,
  SET_TAG_SORT_INFO
} from './types';

import type { Dispatch } from 'redux';
import { log } from '../../../utils/jsUtils';
import SPBackend from '../../../services/SPBackend';
import { setSnackMessage } from '../common';

// Action to initialize the tags in the store
export const setTags = (tags: Array<Object>) => ({
  type: SET_TAGS,
  payload: tags
});

// Action to add a tag to a customer
export const addTag = (customerId: string, tag: Object) => ({
  type: ADD_TAG,
  payload: { customerId, tag }
});

// Action to remove a tag from a customer
export const removeTag = (customerId: string, tag: Object) => ({
  type: DELETE_TAG,
  payload: { customerId, tag }
});

// Action to update a tag's properties
export const updateTag = (tag: Object) => ({
  type: UPDATE_TAG,
  payload: tag
});

// Action to create a new tag
export const createTag = (name: string, color: string = '#007bff') => {
  return async (dispatch: Dispatch<any>) => {
    try {
      // In a real app, you would call the API to create the tag
      // For this example, we'll just create a mock tag with a unique ID
      const newTag = {
        _id: `tag_${Date.now()}`, // Generate a unique ID
        name: name,
        color: color,
        createdAt: new Date().toISOString()
      };
      
      dispatch({
        type: ADD_TAG,
        payload: { tag: newTag }
      });
      
      // Show success notification
      dispatch(setSnackMessage({
        message: 'Tag created successfully',
        level: 'success',
        duration: 2000
      }));
      
      return { payload: newTag };
    } catch (error) {
      log('Error creating tag:', error);
      
      // Show error notification
      dispatch(setSnackMessage({
        message: `Failed to create tag: ${error.message || 'Unknown error'}`,
        level: 'error',
        duration: 3000
      }));
      
      throw error;
    }
  };
};

// Action to show the tag picker for a specific customer
export const showTagPicker = (customerId: string) => ({
  type: SHOW_TAG_PICKER,
  payload: { customerId }
});

// Action to hide the tag picker
export const hideTagPicker = () => ({
  type: HIDE_TAG_PICKER
});

// Action to set whether the tag picker is open
// This controls the global state of the tag picker
export const setTagPickerOpen = (isOpen: boolean, customerId: string) => {
  return (dispatch: Dispatch<any>) => {
    if (isOpen && customerId) {
      dispatch({
        type: SHOW_TAG_PICKER,
        payload: { customerId }
      });
    } else {
      dispatch({
        type: HIDE_TAG_PICKER
      });
    }
  };
};

// Force open the tag picker with a customer ID in a single action
export const forceTagPickerOpen = (customerId: string, isOpen: boolean) => ({
  type: FORCE_TAG_PICKER_OPEN,
  payload: { customerId, open: isOpen }
});

// Action to set tag sort info
export const setSortInfo = (sortInfo: Object) => ({
  type: SET_TAG_SORT_INFO,
  payload: sortInfo
});

// Cache to prevent redundant API calls
// Note: Cache cleared to force refetch with customer data
const tagsCache = {
  tags: null,
  lastFetchTime: 0,
  isFetching: false
};

// Action to fetch tags from backend
export const fetchTags = (user: Object, sortInfo?: Object) => {
  return async (dispatch: Dispatch<any>) => {
    try {
      // If there's no valid user object, don't attempt to fetch
      if (!user || !user.uid) {
        log('[fetchTags] No valid user object provided');
        return { payload: [] };
      }
      
      // If we're already fetching, don't start another fetch
      if (tagsCache.isFetching) {
        log('[fetchTags] Already fetching, skipping redundant API call');
        return { payload: tagsCache.tags || [] };
      }
      
      // Use cached tags if they exist and were fetched recently (within 30 seconds)
      // Note: Clear cache when switching from 'simple' to 'cust' endpoint to ensure customer data is fetched
      const now = Date.now();
      if (tagsCache.tags && (now - tagsCache.lastFetchTime < 30000)) {
        log('[fetchTags] Using cached tags');
        
        // If sortInfo is provided, sort the cached tags
        if (sortInfo) {
          let sortedTags = [...tagsCache.tags];
          const sortKey = Object.keys(sortInfo)[0];
          const sortOrder = sortInfo[sortKey];
          
          if (sortKey && sortOrder) {
            // Create a copy before sorting to avoid mutating a possibly frozen array
            sortedTags = [...sortedTags].sort((a, b) => {
              const aValue = a[sortKey] || '';
              const bValue = b[sortKey] || '';
              return sortOrder * (aValue > bValue ? 1 : aValue < bValue ? -1 : 0);
            });
          }
          
          // Just update the redux store with sorted tags without API call
          dispatch(setTags(sortedTags));
          return { payload: sortedTags };
        }
        
        return { payload: tagsCache.tags };
      }
      
      // Mark that we're fetching
      tagsCache.isFetching = true;
      log('[fetchTags] Fetching tags for user:', user?.uid);
      
      try {
        // Use SPBackend.getTags with the correct parameters
        // Use 'cust' to get customer count data for each tag
        const response = await SPBackend.getTags('cust', user);
        
        // Extract tags from the response
        let tags = response.data || [];
        
        // Update cache
        tagsCache.tags = tags;
        tagsCache.lastFetchTime = now;
        
        // If sortInfo is provided, sort the tags
        if (sortInfo) {
          const sortKey = Object.keys(sortInfo)[0];
          const sortOrder = sortInfo[sortKey];
          
          if (sortKey && sortOrder) {
            // Sort the tags array safely by creating a new array
            tags = [...tags].sort((a, b) => {
              const aValue = a[sortKey] || '';
              const bValue = b[sortKey] || '';
              return sortOrder * (aValue > bValue ? 1 : aValue < bValue ? -1 : 0);
            });
          }
        }
        
        // Dispatch the tags to the store
        dispatch(setTags(tags));
        
        // Return the tags in the payload for any components that need immediate access
        return { payload: tags };
      } catch (error) {
        log('[fetchTags] API call error:', error);
        throw error;
      } finally {
        // Always clear the fetching flag
        tagsCache.isFetching = false;
      }
    } catch (error) {
      // Clear fetching flag on error
      tagsCache.isFetching = false;
      log('Error fetching tags:', error);
      throw error;
    }
  };
};